/**
 * Phase 20F — NL Postcode lookup helper + React hook.
 *
 * Backend proxy: GET /api/admin/postcode-lookup?postal_code=&house_number=
 *
 * Gebruik:
 *   const { status, result } = usePostcodeLookup({
 *     postal_code, house_number, country, enabled: true,
 *   });
 *   // status: "idle" | "loading" | "found" | "not_found" | "error"
 *   // result: { street, city, country, ... } on found
 *
 * Hook trigger-policy:
 *   * alleen wanneer country in NL_COUNTRIES of leeg/onbekend
 *   * alleen wanneer beide velden ≥3 chars en lijken op geldige PC + huisnr
 *   * debounced 600ms zodat we niet bij iedere keystroke een request doen
 *   * stilletjes idle bij ontbrekende auth-token
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { adminApi } from "../context/AuthContext";

const NL_COUNTRIES = new Set(["", "nl", "nld", "nederland", "netherlands", "the netherlands", "holland"]);

const _LOOKUP_DEBOUNCE_MS = 600;

const PUBLIC_BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function isLikelyDutchAddress(postal_code, house_number, country) {
  if (!postal_code || !house_number) return false;
  const c = String(country || "").trim().toLowerCase();
  if (!NL_COUNTRIES.has(c)) return false;
  const pcCleaned = String(postal_code).replace(/[-\s]/g, "").toUpperCase();
  if (!/^[0-9]{4}[A-Z]{2}$/.test(pcCleaned)) return false;
  if (!/^\d+/.test(String(house_number).trim())) return false;
  return true;
}

export async function lookupPostcode(postal_code, house_number, { variant = "admin" } = {}) {
  try {
    if (variant === "public") {
      const r = await axios.get(`${PUBLIC_BACKEND}/api/checkout/postcode-lookup`, {
        params: { postal_code, house_number },
      });
      return r.data;
    }
    const r = await adminApi.get("/admin/postcode-lookup", {
      params: { postal_code, house_number },
    });
    return r.data;
  } catch (err) {
    return { found: false, error: "Lookup-service tijdelijk niet beschikbaar", source: "client" };
  }
}

export function usePostcodeLookup({ postal_code, house_number, country, enabled = true, variant = "admin" }) {
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const cancelRef = useRef(0);

  useEffect(() => {
    if (!enabled) { setStatus("idle"); setResult(null); return undefined; }
    if (!isLikelyDutchAddress(postal_code, house_number, country)) {
      setStatus("idle"); setResult(null); return undefined;
    }
    const myCancelId = ++cancelRef.current;
    setStatus("loading");
    const timer = setTimeout(async () => {
      const r = await lookupPostcode(postal_code, house_number, { variant });
      // Discard if state changed during request
      if (cancelRef.current !== myCancelId) return;
      if (r.found) { setStatus("found"); setResult(r); }
      else if (r.source === "input") { setStatus("idle"); setResult(null); }
      else if (r.error && r.error.includes("niet beschikbaar")) { setStatus("error"); setResult(r); }
      else { setStatus("not_found"); setResult(r); }
    }, _LOOKUP_DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
  }, [postal_code, house_number, country, enabled, variant]);

  return { status, result };
}

/**
 * Returnt de visuele label-tekst per status. Houdt taal en wording op
 * één plek voor consistente UX in NewQuoteDialog + OfferteDetail.
 */
export function postcodeLookupLabel(status, result) {
  if (status === "loading") return { color: "gray", text: "Adres ophalen…" };
  if (status === "found") {
    const city = result?.city ? ` ${result.city}` : "";
    return { color: "green", text: `Adres gevonden:${city}` };
  }
  if (status === "not_found") {
    return { color: "amber", text: result?.error || "Adres niet gevonden. Vul handmatig in." };
  }
  if (status === "error") {
    return { color: "amber", text: result?.error || "Lookup-service tijdelijk niet beschikbaar." };
  }
  return { color: "gray", text: "" };
}
